// Multi-language code samples for the gateway playground "View code" panel.
// Produces cURL / Python / JavaScript for the OpenAI-compatible app.nz gateway,
// plus a Markdown export. The key is the user's real stored key when available,
// otherwise a clearly-placeholder env reference (we can't recover hashed keys).

export type CodeKind = 'curl' | 'python' | 'javascript';

export const CODE_LANGS: { id: CodeKind; label: string }[] = [
  { id: 'python', label: 'Python' },
  { id: 'javascript', label: 'JavaScript' },
  { id: 'curl', label: 'cURL' },
];

export const codeLangHl: Record<CodeKind, string> = {
  curl: 'bash',
  python: 'python',
  javascript: 'javascript',
};

export type GatewaySpec = {
  origin: string;
  path: string; // e.g. /v1/videos/generations
  apiKey: string; // real key if available, '' otherwise
  // sdk shape decides the idiomatic call used in Python/JS:
  sdk: 'chat' | 'image' | 'embeddings' | 'speech' | 'transcription' | 'post';
  body?: Record<string, unknown>;
  langQuery?: string; // transcription ?language=
};

// Placeholder shown when the user has no stored key (e.g. only cookie-logged-in).
const KEY_ENV = 'APPNZ_API_KEY';

function keyLiteral(apiKey: string): { curl: string; py: string; js: string; isReal: boolean } {
  const k = apiKey.trim();
  if (k) return { curl: k, py: `"${k}"`, js: `"${k}"`, isReal: true };
  return {
    curl: `$${KEY_ENV}`,
    py: `os.environ["${KEY_ENV}"]`,
    js: `process.env.${KEY_ENV}`,
    isReal: false,
  };
}

function jsonC(body: unknown, indent: number): string {
  return JSON.stringify(body ?? {}, null, indent);
}

export function buildGatewaySamples(spec: GatewaySpec): Record<CodeKind, string> {
  const { origin, path, apiKey, sdk, body } = spec;
  const base = `${origin}${path}`;
  const baseURL = `${origin}/v1`;
  const relPath = path.replace(/^\/v1/, '');
  const key = keyLiteral(apiKey);

  // ---- cURL ----
  const auth = `-H "Authorization: Bearer ${key.curl}"`;
  const ctype = '-H "Content-Type: application/json"';
  let curl: string;
  if (sdk === 'speech') {
    curl = `curl ${base} \\\n  ${ctype} \\\n  ${auth} \\\n  -d '${jsonC(body, 0)}' \\\n  --output speech.mp3`;
  } else if (sdk === 'transcription') {
    const qs = spec.langQuery ? `?language=${spec.langQuery}` : '';
    curl = `curl ${base}${qs} \\\n  -H "Content-Type: audio/webm" \\\n  ${auth} \\\n  --data-binary @audio.webm`;
  } else {
    curl = `curl ${base} \\\n  ${ctype} \\\n  ${auth} \\\n  -d '${jsonC(body, 0)}'`;
  }

  // ---- Python ----
  const pyHead = `import os\nfrom openai import OpenAI\n\nclient = OpenAI(base_url="${baseURL}", api_key=${key.py})\n`;
  let python: string;
  switch (sdk) {
    case 'chat':
      python = `${pyHead}\nstream = client.chat.completions.create(\n    **${jsonC(body, 4)},\n)\nfor chunk in stream:\n    print(chunk.choices[0].delta.content or "", end="")`;
      break;
    case 'image':
      python = `${pyHead}\nresult = client.images.generate(**${jsonC(body, 4)})\nprint(result.data[0].url)`;
      break;
    case 'embeddings':
      python = `${pyHead}\nresult = client.embeddings.create(**${jsonC(body, 4)})\nprint(result.data[0].embedding[:8])`;
      break;
    case 'speech':
      python = `${pyHead}\nresult = client.audio.speech.create(**${jsonC(body, 4)})\nresult.stream_to_file("speech.mp3")`;
      break;
    case 'transcription':
      python = `${pyHead}\nwith open("audio.webm", "rb") as f:\n    result = client.audio.transcriptions.create(model="whisper-1", file=f)\nprint(result.text)`;
      break;
    default:
      python = `${pyHead}\nresult = client.post(\n    "${relPath}",\n    body=${jsonC(body, 4)},\n    cast_to=dict,\n)\nprint(result.get("video_url") or result)`;
  }

  // ---- JavaScript ----
  const jsHead = `import OpenAI from "openai";\n\nconst client = new OpenAI({ baseURL: "${baseURL}", apiKey: ${key.js} });\n`;
  let javascript: string;
  switch (sdk) {
    case 'chat':
      javascript = `${jsHead}\nconst stream = await client.chat.completions.create(${jsonC(body, 2)});\nfor await (const chunk of stream) {\n  process.stdout.write(chunk.choices[0]?.delta?.content || "");\n}`;
      break;
    case 'image':
      javascript = `${jsHead}\nconst result = await client.images.generate(${jsonC(body, 2)});\nconsole.log(result.data[0].url);`;
      break;
    case 'embeddings':
      javascript = `${jsHead}\nconst result = await client.embeddings.create(${jsonC(body, 2)});\nconsole.log(result.data[0].embedding.slice(0, 8));`;
      break;
    case 'speech':
      javascript = `${jsHead}\nconst result = await client.audio.speech.create(${jsonC(body, 2)});\nconst buf = Buffer.from(await result.arrayBuffer());\nawait (await import("node:fs/promises")).writeFile("speech.mp3", buf);`;
      break;
    case 'transcription':
      javascript = `${jsHead}\nimport fs from "node:fs";\nconst result = await client.audio.transcriptions.create({\n  model: "whisper-1",\n  file: fs.createReadStream("audio.webm"),\n});\nconsole.log(result.text);`;
      break;
    default:
      javascript = `${jsHead}\nconst result = await client.post("${relPath}", {\n  body: ${jsonC(body, 2)},\n  cast_to: Object,\n});\nconsole.log(result.video_url ?? result);`;
  }

  return { curl, python, javascript };
}

export function samplesToMarkdown(samples: Record<CodeKind, string>, title: string): string {
  return [
    `# ${title}`,
    '',
    'Call the app.nz OpenAI-compatible gateway. Set `APPNZ_API_KEY` to a key from https://app.nz/api-keys (or sign in — your session authenticates browser requests automatically).',
    '',
    '## Python',
    '```python',
    samples.python,
    '```',
    '',
    '## JavaScript',
    '```javascript',
    samples.javascript,
    '```',
    '',
    '## cURL',
    '```bash',
    samples.curl,
    '```',
    '',
  ].join('\n');
}
